import type {
  CodeSent,
  EmailCodeSent,
  LoginInput,
  SendSignInCodeInput,
  SignInCodeChannels,
  StartImpersonationInput,
  TwoFactorMethod,
  VerifySignInCodeInput,
} from '@chukua/common';
import { api } from '../../shared.api';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  isActive: boolean;
  /** False for an account that signs in only with codes. Two-factor guards password sign-in, so it needs one. */
  hasPassword: boolean;
  totpEnabled: boolean;
  emailOtpEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  /** The roles the user holds, named, for their account page. Set by an admin. */
  roles: { key: string; name: string }[];
  /** This session signed in with a code to the user's own email or mobile, which acting as another user accepts in place of two-factor. */
  signedInWithCode: boolean;
  /** The union of every role the user holds. Server remains the authority — this is UX only (CLAUDE.md → Web conventions). */
  permissions: string[];
  /** Present only while an admin is acting as this user (testing only, docs: admin impersonation). */
  impersonation?: { impersonatorId: string; impersonatorName: string };
}

/** The account has a second factor on: ask for a code from one of `methods`, the first one first. */
export interface LoginStepUpRequired {
  requiresTwoFactor: true;
  methods: TwoFactorMethod[];
}

/** An account with a second factor on gets `requiresTwoFactor` instead of a user — the same request, no session yet. */
export function login(input: LoginInput): Promise<CurrentUser | LoginStepUpRequired> {
  return api<CurrentUser | LoginStepUpRequired>('/auth/login', { method: 'POST', body: JSON.stringify(input) });
}

/** A 6-digit code is checked against `method`; a recovery code works whichever method is showing. */
export function verifyTwoFactorLogin(code: string, method: TwoFactorMethod): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/two-factor/verify', { method: 'POST', body: JSON.stringify({ code, method }) });
}

/** Emails a code for the login in progress; also "Send another code". A test deployment returns the code instead. */
export function sendLoginEmailCode(): Promise<EmailCodeSent> {
  return api<EmailCodeSent>('/auth/two-factor/email-code', { method: 'POST' });
}

/** Public: the channels a sign-in code can go by right now, email first. Empty when nothing can send, and the login page offers only the password. */
export function getSignInCodeChannels(): Promise<SignInCodeChannels> {
  return api<SignInCodeChannels>('/auth/sign-in/channels');
}

/**
 * Sends a sign-in code to the account's email or mobile by `channel`; also "Send another code" and "Send it by … instead".
 * Answers alike whether or not anyone has that email or number. A test deployment returns the code as well.
 */
export function sendSignInCode(input: SendSignInCodeInput): Promise<CodeSent> {
  return api<CodeSent>('/auth/sign-in/send-code', { method: 'POST', body: JSON.stringify(input) });
}

/** A correct code is the whole sign-in: the user, with the session's cookies, and never a two-factor step. */
export function verifySignInCode(input: VerifySignInCodeInput): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/sign-in/verify', { method: 'POST', body: JSON.stringify(input) });
}

export function logout(): Promise<void> {
  return api<void>('/auth/logout', { method: 'POST' });
}

export function me(): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/me');
}

export function startImpersonation(input: StartImpersonationInput): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/impersonation', { method: 'POST', body: JSON.stringify(input) });
}

export function stopImpersonation(): Promise<CurrentUser> {
  return api<CurrentUser>('/auth/impersonation', { method: 'DELETE' });
}
